import { useState } from "react";
import { Loader2, CalendarX2, Undo2, Gift } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";

// Billing card footer for auto-pay / yearly subscribers: cancel at the end of the paid period (access stays until then), with undo.
export const CancelSubscription = ({ user, refresh }) => {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const ends = user.renews_at ? new Date(user.renews_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "the end of your paid period";
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); await refresh?.(); setConfirm(false); toast.success(msg); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  if (user.cancel_at_period_end) {
    const wb = user.winback;
    return (
      <div className="mt-1 space-y-1.5" data-testid="profile-cancel-pending">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-[#f5a524]/40 bg-[#f5a524]/10 px-3 py-2 text-[11.5px] text-[#f5c542]">
          <CalendarX2 className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="min-w-0 flex-1">Your subscription ends <b>{ends}</b> — full access until then, no further charges.</span>
          {!wb && <button data-testid="profile-resume-btn" disabled={busy} onClick={() => run(api.resumeSubscription, "Your subscription continues — nothing changes.")} className="glass-btn flex items-center gap-1 px-2.5 py-1 text-[11px] text-[#3ecf8e]">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />} Keep my subscription
          </button>}
        </div>
        {wb && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-[#3ecf8e]/40 bg-[#3ecf8e]/10 px-3 py-2 text-[11.5px] text-[#9fe8c5]" data-testid="profile-winback">
            <Gift className="h-3.5 w-3.5 flex-shrink-0 text-[#3ecf8e]" />
            <span className="min-w-0 flex-1">A personal price if you stay: <b className="text-white">{wb.label}</b>{wb.expires_at ? ` · valid until ${new Date(wb.expires_at).toLocaleDateString(undefined, { month: "long", day: "numeric" })}` : ""}</span>
            <button data-testid="profile-winback-accept-btn" disabled={busy} onClick={() => run(api.resumeSubscription, `You're staying — ${wb.label} is on your subscription.`)} className="flex items-center gap-1 rounded-md border border-[#3ecf8e]/60 bg-[#3ecf8e]/25 px-3 py-1 text-[11.5px] font-semibold text-white transition-colors hover:bg-[#3ecf8e]/40">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />} Keep my subscription at {wb.short}
            </button>
          </div>
        )}
      </div>
    );
  }
  if (!confirm) {
    return (
      <div className="mt-1 flex items-center justify-between gap-3 text-[11px] text-[#6b7079]" data-testid="profile-cancel-row">
        <span>Changed your mind? You can stop renewing any time.</span>
        <button data-testid="profile-cancel-btn" onClick={() => setConfirm(true)} className="flex-shrink-0 text-[#9aa0a8] underline-offset-2 transition-colors hover:text-[#ff8a8e] hover:underline">Cancel subscription</button>
      </div>
    );
  }
  return (
    <div className="mt-1 space-y-2 rounded-lg border border-[#e5484d]/40 bg-[#e5484d]/10 px-3 py-2.5 text-[11.5px] text-[#ffb4b6]" data-testid="profile-cancel-confirm">
      <div>Cancel at the end of your paid period? You keep cutting until <b>{ends}</b>; after that your plan ends and you will not be charged again. Your patterns and plotter settings stay saved if you come back.</div>
      <div className="flex flex-wrap gap-2">
        <button data-testid="profile-cancel-confirm-btn" disabled={busy} onClick={() => run(api.cancelSubscription, `Done — your subscription ends ${ends}.`)} className="flex items-center gap-1 rounded-md border border-[#e5484d]/60 bg-[#e5484d]/25 px-3 py-1 text-[11.5px] font-semibold text-white transition-colors hover:bg-[#e5484d]/40">
          {busy && <Loader2 className="h-3 w-3 animate-spin" />} Yes, cancel on {ends}
        </button>
        <button data-testid="profile-cancel-keep-btn" disabled={busy} onClick={() => setConfirm(false)} className="glass-btn px-3 py-1 text-[11.5px] text-[#e8e9ec]">Keep my plan</button>
      </div>
    </div>
  );
};
